import { computed, observable, when, makeObservable, actionBound } from "mobx";
import downloadFile from "@qn/services/api/downloadFile";
import { retry } from "@/utils/retry";
import getCloudFileUrl from "@qn/services/api/getCloudFileUrl";

/** 为避免CDN的缓存，如果OSS的资源发生改变但没有加载到最新版本，可更新时间戳前端发布一个新版本刷新缓存,且加上参数后，千牛端很少出现乱码的情况 */
const lastUpdateTime = "1751534431700";
const fhyAreaUrl = "https://cdn.fahuoyi.com/areas/fhy_area.json" + `?v=${lastUpdateTime}`;

export interface FhyArea {
    id: number;
    name: string;
    aliases?: string[];
    children?: FhyArea[];
}

interface FhyAreaInfo {
    name: string;
    type: "province" | "city" | "district";
    children?: FhyArea[];
    relation: number[];
}

class AreaStore {
    constructor() {
        makeObservable<AreaStore, "loadFromQN" | "loadFromUrl">(this, {
            loading: observable,
            fhyAreas: observable,
            fhyAreaMap: computed,
            loadFhyArea: actionBound,
            loadFromQN: actionBound,
            loadFromUrl: actionBound,
            ensureFhyArea: actionBound,
        });
    }

    loading = false;

    fhyAreas: FhyArea[] = [];

    get fhyAreaMap() {
        const map = new Map<number, FhyAreaInfo>();

        this.fhyAreas.map((province) => {
            map.set(province.id, {
                name: province.name,
                type: "province",
                children: province.children,
                relation: [],
            });

            province?.children.map((city) => {
                map.set(city.id, {
                    name: city.name,
                    type: "city",
                    children: city.children,
                    relation: [province.id],
                });

                city?.children.map((district) => {
                    map.set(district.id, {
                        name: district.name,
                        type: "district",
                        relation: [province.id, city.id],
                    });
                });
            });
        });

        return map;
    }

    async loadFhyArea() {
        this.loading = true;

        try {
            const success = import.meta.env.VITE_IS_QN ? await this.loadFromQN() : await this.loadFromUrl(fhyAreaUrl);

            if (!success) {
                console.warn("加载 FHY 地区数据失败");
            }
        } finally {
            this.loading = false;
        }
    }

    private async loadFromQN(): Promise<boolean> {
        // 优先尝试代理URL(千牛环境从阿里CDN中加载可能会出现乱码，但是OSS直接从OSS中不会)
        const proxyUrl = await downloadFile(fhyAreaUrl);
        if (await this.loadFromUrl(proxyUrl, "代理URL")) return true;

        // 回退到云端URL
        const cloudUrl = await getCloudFileUrl("cloud://DC2E28DAAB50529E9817E7F39BADCDF9/fhy_area.json");
        return this.loadFromUrl(cloudUrl, "云端URL");
    }

    private async loadFromUrl(url: string, label = "默认URL"): Promise<boolean> {
        try {
            const data = await fetch(url).then((res) => res.json());

            if (Array.isArray(data)) {
                this.fhyAreas = data;

                console.log(`[${label}] 地址加载成功:`, data);

                return true;
            } else {
                console.warn(`[${label}] 数据格式非法`, data);
            }
        } catch (error) {
            console.error(`[${label}] 加载失败: ${url}`, error);
        }

        return false;
    }
    async ensureFhyArea() {
        await when(() => !this.loading);

        if (this.fhyAreas?.length) return;

        await retry(this.loadFhyArea, 2);
    }
}

export default AreaStore;
